import { useCallback, useEffect, useRef, useState } from 'react';
import { io } from 'socket.io-client';
import { API_SERVER } from '../config/env';
import { getAuthToken } from '../services/authSession';

const SOCKET_URL = typeof API_SERVER === 'string' ? API_SERVER.trim().replace(/\/+$/, '') : '';

/**
 * Conecta ao Socket.IO do server-eurobem, entra na sala do broker e chama onUpdate (debounced).
 * @param {'awin'|'lomadee'} broker
 * @param {{ onUpdate?: (payload: object) => void, enabled?: boolean }} [options]
 */
export function useBrokerQueueSocket(broker, options = {}) {
    const { onUpdate, enabled = true } = options;
    const [connected, setConnected] = useState(false);
    const onUpdateRef = useRef(onUpdate);
    const debounceRef = useRef(null);

    useEffect(() => {
        onUpdateRef.current = onUpdate;
    }, [onUpdate]);

    const room = broker === 'lomadee' ? 'broker:lomadee' : 'broker:awin';

    const flushUpdate = useCallback(payload => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            onUpdateRef.current?.(payload);
        }, 350);
    }, []);

    useEffect(() => {
        if (!enabled || !SOCKET_URL || !broker) {
            setConnected(false);
            return undefined;
        }

        const token = getAuthToken();
        if (!token) {
            setConnected(false);
            return undefined;
        }

        const socket = io(SOCKET_URL, {
            auth: { token },
            transports: ['websocket'],
            reconnection: true,
            reconnectionAttempts: 12,
            reconnectionDelay: 1500,
        });

        const onConnect = () => {
            setConnected(true);
            socket.emit('join:room', room);
        };

        const onDisconnect = () => setConnected(false);

        const onQueueUpdate = payload => {
            if (payload?.broker && payload.broker !== broker) return;
            flushUpdate(payload);
        };

        socket.on('connect', onConnect);
        socket.on('disconnect', onDisconnect);
        socket.on('broker:queue:update', onQueueUpdate);

        if (socket.connected) onConnect();

        return () => {
            if (debounceRef.current) clearTimeout(debounceRef.current);
            socket.emit('leave:room', room);
            socket.off('connect', onConnect);
            socket.off('disconnect', onDisconnect);
            socket.off('broker:queue:update', onQueueUpdate);
            socket.disconnect();
            setConnected(false);
        };
    }, [broker, enabled, room, flushUpdate]);

    return { connected, room };
}

export default useBrokerQueueSocket;
